import React, { useState } from "react";
import axios from "axios";
import toast from "react-hot-toast";
import { Modal } from "react-bootstrap";
import { useModalDismiss } from "./useModalDismiss";

interface DocumentItem {
    id: string;
    filename: string;
    categoryId: string;
    folderId: string | null;
    isPinned: boolean;
    sortOrder: number;
    createdAt: string;
    updatedAt: string;
    createdBy: string | null;
}

interface Props {
    doc: DocumentItem;
    onSave: () => void;
    onCancel: () => void;
}

function splitExt(filename: string): { base: string; ext: string } {
    const dot = filename.lastIndexOf(".");
    if (dot <= 0 || dot === filename.length - 1) return { base: filename, ext: "" };
    return { base: filename.slice(0, dot), ext: filename.slice(dot) };
}

export default function DocumentRenameForm({ doc, onSave, onCancel }: Props) {
    const { base: initialBase, ext } = splitExt(doc.filename);
    const [base, setBase] = useState(initialBase);
    const [saving, setSaving] = useState(false);
    const { show, dismiss, onExited } = useModalDismiss(onCancel, onSave);

    function handleSubmit(e: React.FormEvent) {
        e.preventDefault();
        const trimmed = base.trim();
        if (!trimmed) { toast.error("Filename is required"); return; }
        const name = trimmed + ext;
        setSaving(true);
        axios.put(`/api/manuals/documents/${doc.id}`, {
            categoryId: doc.categoryId,
            folderId: doc.folderId,
            filename: name,
            isPinned: doc.isPinned,
            sortOrder: doc.sortOrder,
        })
            .then(res => {
                if (res.data.success) {
                    toast.success("Document renamed");
                    dismiss("save");
                } else {
                    toast.error(res.data.message || "Failed to rename");
                    setSaving(false);
                }
            })
            .catch(err => {
                toast.error(err.response?.data?.message || "Failed to rename");
                setSaving(false);
            });
    }

    return (
        <Modal show={show} onHide={() => dismiss("cancel")} onExited={onExited} centered backdropClassName="m-modal-backdrop">
            <form onSubmit={handleSubmit}>
                <Modal.Header closeButton>
                    <Modal.Title style={{ fontSize: 17, fontWeight: 600 }}>Rename document</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <div className="mb-3">
                        <label className="m-label">Filename</label>
                        <div className="m-rename-field">
                            <input
                                type="text"
                                className="m-input m-rename-input"
                                value={base}
                                onChange={e => setBase(e.target.value)}
                                maxLength={512}
                                autoFocus
                            />
                            {ext && <span className="m-rename-ext">{ext}</span>}
                        </div>
                    </div>
                </Modal.Body>
                <Modal.Footer>
                    <button type="button" className="m-btn m-btn-ghost" onClick={() => dismiss("cancel")}>Cancel</button>
                    <button type="submit" className="m-btn m-btn-primary" disabled={saving}>
                        {saving ? "Saving..." : "Rename document"}
                    </button>
                </Modal.Footer>
            </form>
        </Modal>
    );
}
